/**
 * Hook for fetching personal hours dashboard data (histogram).
 * Returns aggregated hours with category breakdowns.
 * Supports grouping by day, month, quarter, or year.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

export type GroupBy = 'day' | 'month' | 'quarter' | 'year';

export interface DashboardMonth {
  month: string; // key varies: "YYYY-MM-DD" | "YYYY-MM" | "YYYY-Q1" | "YYYY"
  categories: Record<string, number>;
  total_hours: number;
}

export interface DashboardData {
  months: DashboardMonth[];
  group_by: GroupBy;
}

export interface UseDashboardReturn {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  refresh: (months?: number, groupBy?: GroupBy) => Promise<void>;
}

export function useDashboard(initialMonths = 12, initialGroupBy: GroupBy = 'month'): UseDashboardReturn {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(
    async (months = initialMonths, groupBy = initialGroupBy) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiFetch<DashboardData>(`/api/punch/dashboard?months=${months}&group_by=${groupBy}`);
        setData(result);
      } catch {
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    },
    [initialMonths, initialGroupBy],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
